import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { AlertCircle, Calculator, CheckCircle, DollarSign, Eye, TrendingUp, Users } from 'lucide-react';

export default function AdRevenueTab({ adRevenueProjections, adAssumptions, getStatusBadge }) {
  return (
    <>
              {/* Key Metrics */}
              <div className="grid md:grid-cols-4 gap-4">
                <Card className="bg-emerald-900/10 border-emerald-700/30">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <DollarSign className="w-5 h-5 text-emerald-600" />
                      <span className="text-xs text-emerald-600">Current Phase</span>
                    </div>
                    <p className="text-2xl font-bold mt-2">Beta</p>
                    <p className="text-xs text-emerald-600">~156 users</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-blue-900/10 border-blue-700/30">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <Eye className="w-5 h-5 text-blue-600" />
                      <span className="text-xs text-blue-600">Est. CPM</span>
                    </div>
                    <p className="text-2xl font-bold mt-2">$2.50-$5.00</p>
                    <p className="text-xs text-blue-600">Pet niche premium</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-purple-900/10 border-purple-700/30">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <TrendingUp className="w-5 h-5 text-purple-600" />
                      <span className="text-xs text-purple-600">Target RPM</span>
                    </div>
                    <p className="text-2xl font-bold mt-2">$8-$18</p>
                    <p className="text-xs text-purple-600">Revenue per 1K views</p>
                  </CardContent>
                </Card>
                
                <Card className="bg-amber-900/10 border-amber-700/30">
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <Users className="w-5 h-5 text-amber-600" />
                      <span className="text-xs text-amber-600">Ad Eligible</span>
                    </div>
                    <p className="text-2xl font-bold mt-2">70%</p>
                    <p className="text-xs text-amber-600">Guest visitors only</p>
                  </CardContent>
                </Card>
              </div>

              {/* Revenue Projections Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calculator className="w-5 h-5" />
                    Ad Revenue Projections by Scale
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>MAU</TableHead>
                        <TableHead>Page Views/mo</TableHead>
                        <TableHead>Ad Impressions</TableHead>
                        <TableHead>CPM</TableHead>
                        <TableHead>Monthly Revenue</TableHead>
                        <TableHead>Annual Revenue</TableHead>
                        <TableHead>Phase</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {adRevenueProjections.map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{row.mau.toLocaleString()}</TableCell>
                          <TableCell>{row.pageViews.toLocaleString()}</TableCell>
                          <TableCell>{row.adImpressions.toLocaleString()}</TableCell>
                          <TableCell>${row.cpm.toFixed(2)}</TableCell>
                          <TableCell className="font-medium text-green-600">${row.monthlyRevenue.toLocaleString()}</TableCell>
                          <TableCell className="font-medium text-green-600">${row.annualRevenue.toLocaleString()}</TableCell>
                          <TableCell>{getStatusBadge(row.status)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Assumptions */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="w-5 h-5" />
                    Assumptions & Notes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid md:grid-cols-2 gap-4">
                    {adAssumptions.map((item, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                        <span className="text-sm">{item.label}</span>
                        <span className="font-medium text-sm">{item.value}</span>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div>
                        <p className="font-medium">Key Revenue Drivers</p>
                        <ul className="text-sm mt-2 space-y-1 text-muted-foreground">
                          <li>- Pet owner audience commands premium CPM rates ($3-8 vs $1-2 general)</li>
                          <li>- Guest-only ads preserve member value (ad-free experience for paid members)</li>
                          <li>- High engagement niche content increases page views per session</li>
                          <li>- SEO-friendly content attracts organic traffic</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                      <div>
                        <p className="font-medium">Important Caveats</p>
                        <ul className="text-sm mt-2 space-y-1 text-muted-foreground">
                          <li>- Actual revenue depends on ad fill rate and geographic distribution</li>
                          <li>- Q4 typically sees 20-40% higher CPMs (holiday advertising)</li>
                          <li>- Ad blockers may reduce effective impressions by 15-30%</li>
                          <li>- Premium placements (homepage hero) yield higher rates</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
    </>
  );
}
